import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function AcceptInvite() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<{
    email: string;
    empresaNome?: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    nome_completo: "",
    password: "",
    confirmPassword: "",
  });

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      toast({
        title: "Link inválido",
        description: "O link de convite está inválido ou expirado.",
        variant: "destructive",
      });
      navigate("/auth/login");
      return;
    }

    // Set email from URL
    setInviteInfo({ email });
    setLoadingInvite(false);
  }, [token, email, navigate, toast]);

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !email) {
      toast({
        title: "Erro",
        description: "Token ou email não encontrado",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: {
          token,
          email,
          password: formData.password,
          nome_completo: formData.nome_completo || undefined,
        },
      });

      if (error) {
        console.error("Accept invite error details:", {
          error,
          message: error.message,
          context: error.context,
        });
        
        // Try to extract detailed error message
        let errorMessage = error.message || "Erro ao aceitar convite";
        if (error.context?.msg) {
          errorMessage = error.context.msg;
        } else if (data?.error) {
          errorMessage = data.error;
          if (data.details) {
            errorMessage += `: ${data.details}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      if (!data?.success) {
        const errorMessage = data?.error || data?.details || "Erro ao aceitar convite";
        console.error("Accept invite failed:", data);
        throw new Error(errorMessage);
      }

      toast({
        title: "Sucesso!",
        description: "Convite aceito com sucesso! Você receberá um email de confirmação.",
      });

      // Try to sign in automatically
      try {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: formData.password,
        });

        if (!signInError && signInData.user) {
          // Redirect to dashboard after 1 second
          setTimeout(() => {
            navigate("/dashboard");
          }, 1000);
        } else {
          // Redirect to login if auto sign-in fails
          setTimeout(() => {
            navigate("/auth/login");
          }, 2000);
        }
      } catch (signInError) {
        // Redirect to login if auto sign-in fails
        setTimeout(() => {
          navigate("/auth/login");
        }, 2000);
      }
    } catch (error: any) {
      console.error("Accept invite error:", error);
      toast({
        title: "Erro ao aceitar convite",
        description: error.message || "Ocorreu um erro inesperado. Verifique se o link não expirou.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvite || !token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center">
            <p className="text-muted-foreground">Carregando convite...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/">
            <h1 className="text-3xl font-bold bg-hero-gradient bg-clip-text text-transparent">
              Orbita Control IA
            </h1>
          </Link>
        </div>
        
        <Card className="border-border">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Aceitar Convite</CardTitle>
            <CardDescription>
              {inviteInfo?.empresaNome 
                ? `Você foi convidado para fazer parte de ${inviteInfo.empresaNome}`
                : "Complete seu cadastro para aceitar o convite"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAcceptInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email || ""}
                  disabled
                  className="bg-muted border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome_completo">Nome Completo (Opcional)</Label>
                <Input
                  id="nome_completo"
                  type="text"
                  placeholder="João Silva"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({...formData, nome_completo: e.target.value})}
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="bg-input border-border"
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">
                  A senha deve ter pelo menos 6 caracteres
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  className="bg-input border-border"
                  minLength={6}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-primary"
                disabled={loading}
              >
                {loading ? "Aceitando convite..." : "Aceitar Convite"}
              </Button>
            </form>
            
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Já tem uma conta? </span>
              <Link to="/auth/login" className="text-primary hover:underline">
                Faça login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

